import { ShellEvent } from '@appd/fso-ui-shell/container';
import { DEFAULT_TIME_RANGE_SINCE, DEFAULT_TIME_RANGE_UNTIL } from '../constants';
import { TimeRangeSelector } from '@appd/uik/@future';

export const getEffectiveTimeRangeFromGlobalsVariables = (variablesFromURL: Record<string, unknown>) => {
  const effectiveTimeRange = {
    input: {
      since: (variablesFromURL?.since || DEFAULT_TIME_RANGE_SINCE) as string,
      until: (variablesFromURL?.until || DEFAULT_TIME_RANGE_UNTIL) as string,
    },
    now: new Date().toISOString(),
  };
  return effectiveTimeRange;
};

export const getEffectiveTimeRangeFromURL = (
  globals: Readonly<Record<string, unknown>>,
): Record<string, string | undefined> => {
  const searchParams = new URLSearchParams(window.location.search);
  const since =
    ((globals.variables as Record<string, unknown>)?.since as string) ??
    searchParams.get('since') ??
    DEFAULT_TIME_RANGE_SINCE;
  const until = ((globals.variables as Record<string, unknown>)?.until as string) ?? searchParams.get('until');
  return { since, until };
};

export const toDurationInMinutes = (value: string): number | undefined => {
  const preset = timerangePresets.find(preset => preset.since === value || preset.ptFormat === value);
  return preset ? preset.durationInMinutes : undefined;
};

export const toTimeRangeDisplayLabel = (value: string): string | undefined => {
  const preset = timerangePresets.find(preset => preset.since === value || preset.ptFormat === value);
  return preset ? preset.label : '';
};

export const timerangePresets = [
  {
    label: 'Last 5 Minutes',
    since: 'now -5m',
    ptFormat: '-PT5M',
    durationInMinutes: 5,
    title: 'Last 5 Minutes',
  },
  {
    label: 'Last 15 Minutes',
    since: 'now -15m',
    ptFormat: '-PT15M',
    durationInMinutes: 15,
    title: 'Last 15 Minutes',
  },
  {
    label: 'Last 1 Hour',
    since: 'now -1h',
    ptFormat: '-PT1H',
    durationInMinutes: 60,
    title: 'Last 1 Hour',
  },
  {
    label: 'Last 3 Hours',
    since: 'now -3h',
    ptFormat: '-PT3H',
    durationInMinutes: 60 * 3,
    title: 'Last 3 Hours',
  },
  {
    label: 'Last 6 Hours',
    since: 'now -6h',
    ptFormat: '-PT6H',
    durationInMinutes: 60 * 6,
    title: 'Last 6 Hours',
  },
  {
    label: 'Last 12 Hours',
    since: 'now -12h',
    ptFormat: '-PT12H',
    durationInMinutes: 60 * 12,
    title: 'Last 12 Hours',
  },
  {
    label: 'Last 1 Day',
    since: 'now -1d',
    ptFormat: '-P1D',
    durationInMinutes: 60 * 24,
    title: 'Last 1 Day',
  },
  {
    label: 'Last 3 Days',
    since: 'now -3d',
    ptFormat: '-P3D',
    durationInMinutes: 60 * 24 * 3,
    title: 'Last 3 Days',
  },
  {
    label: 'Last 14 Days',
    since: 'now - 14d',
    ptFormat: '-P14D',
    durationInMinutes: 60 * 24 * 14,
    title: 'Last 14 Days',
  },
  {
    label: 'Last 1 Week',
    since: 'now -1w',
    ptFormat: '-P1W',
    durationInMinutes: 60 * 24 * 7,
    title: 'Last 1 Week',
  },
  {
    label: 'Last 2 Weeks',
    since: 'now -2w',
    ptFormat: '-P2W',
    durationInMinutes: 60 * 24 * 7 * 2,
    title: 'Last 2 Weeks',
  },
  {
    label: 'Last 1 Month',
    since: 'now -30d',
    ptFormat: '-P30D',
    durationInMinutes: 60 * 24 * 30,
    title: 'Last 1 Month',
  },
  {
    label: 'Last 3 Months',
    since: 'now -90d',
    ptFormat: '-P90D',
    durationInMinutes: 60 * 24 * 90,
    title: 'Last 3 Months',
  },
  {
    label: 'Last 6 Months',
    since: 'now -180d',
    ptFormat: '-P180D',
    durationInMinutes: 60 * 24 * 180,
    title: 'Last 6 Months',
  },
  {
    label: 'Last 1 Year',
    since: 'now -365d',
    ptFormat: '-P365D',
    durationInMinutes: 60 * 24 * 365,
    title: 'Last 1 Year',
  },
];

export const getAbsoluteTimeRange = (since: string, until: string | undefined) => {
  const timeRange: TimeRangeSelector.TimeRange.Value = {
    now: new Date().toISOString() as string,
    input: { since, ...(until != null && { until }) },
  };
  return TimeRangeSelector.TimeRange.resolve(timeRange);
};

export const toISO8601StringForCEST = (
  day: number,
  month: number,
  year: number,
  hour: number,
  minute: number,
  second: number,
): string => {
  const parisDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  // paris is in CEST (UTC+2) during July / Aug
  const parisOffset = 2 * 60 * 60 * 1000;
  const utcDate = new Date(parisDate.getTime() - parisOffset);
  return utcDate.toISOString();
};

export const toTimeRangeDisplayLocal = (since: string, until: string): string => {
  const presetTimeRange = toTimeRangeDisplayLabel(since);
  if (presetTimeRange) {
    return `${toTimeRangeDisplayLabel(since)}`;
  }
  const absoluteTimeRange = getAbsoluteTimeRange(since, until);
  const sinceUTC = new Date(absoluteTimeRange.since);
  const untilUTC = new Date(absoluteTimeRange.until);
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  };
  const sinceLocal = new Intl.DateTimeFormat('en-US', options).format(sinceUTC).replace(' at ', ' ');
  const untilLocal = new Intl.DateTimeFormat('en-US', options).format(untilUTC).replace(' at ', ' ');
  const timeZoneFormatter = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' });
  const timeZone = timeZoneFormatter.formatToParts(sinceUTC).find(part => part.type === 'timeZoneName')?.value;
  return `${sinceLocal} - ${until == 'now' ? 'Now' : untilLocal} ${timeZone}`;
};

export const emitTimeRangeSelectorEvent = (since: string, until: string) => {
  const defaultTimeRangeValue: TimeRangeSelector.TimeRange.Value = {
    now: new Date().toISOString(),
    input: { since: since, until: until },
  };
  ShellEvent.emit('stateTimeRangeUpdated', defaultTimeRangeValue);
};
